import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMainStore from '../../store/mainState';
import { ReactComponent as FullScreenIcon } from "../../assets/icons/fullScreen.svg"
import { ReactComponent as FavoriteIcon } from "../../assets/icons/favorites.svg"
import { ReactComponent as LockIcon } from '../../assets/icons/lock.svg';

import { useMutation } from '@apollo/client';
import { ADD_FAVORITE, ENTER_GAME, REMOVE_FAVORITE } from '../../graphql/games';
import GButton from '../../components/ui/base/GButton';
import { isGameAvailableInUserCountry } from '../../utils/gameAvailability';

const Game = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { gameState, setGameState, labelData, user, setIsSignInOpen, userCountry } = useMainStore();
    const isAvailable = isGameAvailableInUserCountry(gameState?.game, userCountry);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const fullscreenRef = useRef<HTMLDivElement | null>(null);
    const [isFavorite, setIsFavorite] = useState(gameState?.game?.isFavorite);
    const [showIframe, setShowIframe] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [isRestricted, setIsRestricted] = useState(false);
    const [showToolbar, setShowToolbar] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const toolbarTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [addFavorite] = useMutation(ADD_FAVORITE);
    const [removeFavorite] = useMutation(REMOVE_FAVORITE);
    const [enterGame] = useMutation(ENTER_GAME);
    const [enterError, setEnterError] = useState<string | null>(null);
    const hasEnteredRef = useRef(false);

    // Auto-hide toolbar after 3 seconds, show on mouse move near top
    const resetToolbarTimer = () => {
        setShowToolbar(true);
        if (toolbarTimeout.current) clearTimeout(toolbarTimeout.current);
        toolbarTimeout.current = setTimeout(() => setShowToolbar(false), 3000);
    };

    useEffect(() => {
        resetToolbarTimer();
        return () => { if (toolbarTimeout.current) clearTimeout(toolbarTimeout.current); };
    }, []);

    useEffect(() => {
        setIsFavorite(gameState?.game?.isFavorite);
    }, [gameState]);

    useEffect(() => {
        const state = location.state as any;
        if (!state?.game) {
            navigate('/casino');
            return;
        }

        setGameState(state);
        setShowIframe(false);
        setIframeLoaded(false);
        setFadeOut(false);
        setIsRestricted(false);
        setEnterError(null);

        const game = state.game;
        const alreadyHasLaunch = !!state.launchUrl;
        const needsEnterGame = !alreadyHasLaunch && isGameAvailableInUserCountry(game, userCountry);

        if (!needsEnterGame) {
            const timer = setTimeout(() => setShowIframe(true), 500);
            return () => clearTimeout(timer);
        }

        if (!user) {
            setIsSignInOpen(true);
            navigate(-1);
            return;
        }

        if (hasEnteredRef.current) return;
        hasEnteredRef.current = true;

        (async () => {
            try {
                const response = await enterGame({
                    variables: {
                        input: {
                            userId: user?.id,
                            gameId: game.gameId,
                            redirectUrl: window.location.origin + '/casino',
                            providerId: Number(game.providerId),
                        },
                    },
                });
                const data = response?.data?.enterGame;
                const gqlError = response?.errors?.[0]?.message;
                if (gqlError || !data?.launchUrl) {
                    hasEnteredRef.current = false;
                    setEnterError(gqlError || 'Failed to enter game');
                    return;
                }
                setGameState({ ...data, game });
                setShowIframe(true);
            } catch (error: any) {
                hasEnteredRef.current = false;
                const message =
                    error?.graphQLErrors?.[0]?.message ||
                    error?.networkError?.result?.errors?.[0]?.message ||
                    error?.message ||
                    'Failed to enter game';
                setEnterError(message);
            }
        })();
    }, [location.state, navigate]);

    useEffect(() => {
        const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    // Listen for postMessage from iframe (some providers send restriction messages)
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const msg = typeof event.data === 'string' ? event.data.toLowerCase() : JSON.stringify(event.data || '').toLowerCase();
            if (msg.includes('restrict') || msg.includes('blocked') || msg.includes('not available') || msg.includes('forbidden') || msg.includes('geo') || msg.includes('country')) {
                setIsRestricted(true);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const handleIframeLoad = () => {
        setIframeLoaded(true);
        // Small extra delay so the game's own resources can render
        setTimeout(() => setFadeOut(true), 800);
    };

    if (!gameState) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex flex-col items-center gap-[16px]">
                    <div className="w-[44px] h-[44px] border-[3px] border-white/10 border-t-[#0086D3] rounded-full animate-spin shadow-[0_0_15px_rgba(0,134,211,0.3)]"></div>
                    <p className="text-white/40 text-[14px] font-medium">{t('game.loadingGame')}</p>
                </div>
            </div>
        );
    }

    const toggleFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else if (fullscreenRef.current?.requestFullscreen) {
            fullscreenRef.current.requestFullscreen();
        }
    };

    const handleFavorite = async () => {
        try {
            if (isFavorite) {
                await removeFavorite({ variables: { gameId: Number(gameState.game.id), labelId: Number(labelData?.id) } });
                setIsFavorite(false);
            } else {
                await addFavorite({ variables: { gameId: Number(gameState.game.id), labelId: Number(labelData?.id) } });
                setIsFavorite(true);
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };

    const handleClose = () => {
        navigate(-1);
    };

    return (
        <div
            ref={fullscreenRef}
            className="w-full h-[calc(100vh-60px)] relative bg-[#0a0e1a] fullscreen:h-screen"
            onMouseMove={(e) => {
                // Show toolbar when mouse is near the top 60px
                if (e.clientY < 120) resetToolbarTimer();
            }}
        >
            <div
                className={`absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-2 transition-all duration-300 
                    opacity-100 translate-y-0' 
                `}
                style={{
                    background: 'linear-gradient(to bottom, rgba(10, 14, 26, 0.95), rgba(10, 14, 26, 0.7), transparent)',
                }}
                onMouseEnter={() => {
                    setShowToolbar(true);
                    if (toolbarTimeout.current) clearTimeout(toolbarTimeout.current);
                }}
                onMouseLeave={resetToolbarTimer}
            >
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/[0.1] active:scale-95"
                    title={t('game.closeGame')}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    <span className="text-white text-sm font-medium hidden sm:inline">{t('game.close')}</span>
                </button>

                {/* Game name */}
                <div className="flex items-center gap-2 flex-1 justify-center min-w-0 px-4">
                    {gameState.game?.iconUrl && (
                        <img src={gameState.game.iconUrl} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                    )}
                    <p className="text-white text-sm font-semibold truncate">{gameState.game?.name}</p>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                    <button
                        onClick={handleFavorite}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.12] transition-all duration-200"
                        title={isFavorite ? t('game.removeFromFavorites') : t('game.addToFavorites')}
                    >
                        <FavoriteIcon width={16} height={13} fill={isFavorite ? '#FF0004' : ''} />
                    </button>
                    <button
                        onClick={toggleFullscreen}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.12] transition-all duration-200"
                        title={isFullscreen ? t('game.exitFullscreen') : t('game.fullscreen')}
                    >
                        <FullScreenIcon width={16} height={16} />
                    </button>
                </div>
            </div>

            {/* Game content - full height */}
            <div className="relative w-full h-full">
                {isAvailable && !isRestricted ?
                    showIframe && (
                        <iframe
                            src={gameState.launchUrl}
                            width="100%"
                            height="100%"
                            ref={iframeRef}
                            onLoad={handleIframeLoad}
                            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation allow-popups-to-escape-sandbox"
                        />
                    ) : (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0a0e1a]">
                            <div className="flex flex-col items-center gap-5 max-w-sm mx-4 p-8 rounded-2xl text-center"
                                style={{
                                    background: 'rgba(20, 30, 48, 0.9)',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
                                }}>
                                <div className="w-16 h-16 rounded-full flex items-center justify-center"
                                    style={{ background: 'rgba(217, 105, 105, 0.12)', border: '1px solid rgba(217, 105, 105, 0.2)' }}>
                                    <LockIcon width="28" height="28" fill="#d96969" />
                                </div>
                                <div>
                                    <h3 className="text-white text-lg font-bold mb-2">{t('game.gameUnavailable')}</h3>
                                    <p className="text-white/50 text-sm leading-relaxed">
                                        {t('game.gameUnavailableDesc')}
                                    </p>
                                </div>
                                <div className="flex gap-3 w-full mt-2">
                                    <GButton className="flex-1 py-3" onClick={() => navigate(-1)} isGradient={true}>
                                        {t('game.goBack')}
                                    </GButton>
                                    <GButton className="flex-1 py-3" onClick={() => navigate('/')} variant="outline">
                                        {t('game.home')}
                                    </GButton>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Error overlay */}
                {enterError && isAvailable && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#0a0e1a] px-[20px]">
                        <div className="w-[80px] h-[80px] rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-[20px]">
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <h2 className="text-white text-[20px] font-bold mb-[8px] text-center">
                            {/Insufficient funds/i.test(enterError) ? t('game.insufficientFunds') : t('game.unableToStart')}
                        </h2>
                        <p className="text-white/40 text-[14px] text-center max-w-[320px] mb-[24px]">
                            {/Insufficient funds/i.test(enterError)
                                ? t('game.topUpAccount')
                                : enterError}
                        </p>
                        <div className="flex gap-[12px]">
                            <GButton className="px-[28px] py-[12px]" onClick={() => navigate('/payment')} isGradient={true}>
                                {t('game.topUp')}
                            </GButton>
                            <GButton className="px-[28px] py-[12px]" onClick={() => navigate(-1)} variant="outline">
                                {t('game.goBack')}
                            </GButton>
                        </div>
                    </div>
                )}

                {/* Loader overlay */}
                {!enterError && !fadeOut && isAvailable && (
                    <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#0a0e1a] transition-opacity duration-500 ${iframeLoaded ? 'opacity-80' : 'opacity-100'}`}>
                        <div className="flex flex-col items-center gap-[24px]">
                            {gameState.game?.iconUrl && (
                                <img src={gameState.game.iconUrl} alt="" className="w-[80px] h-[80px] rounded-[16px] object-cover shadow-[0_4px_30px_rgba(0,0,0,0.5)]" />
                            )}
                            <div className="relative">
                                <div className="w-[48px] h-[48px] border-[3px] border-white/[0.06] border-t-[#0086D3] rounded-full animate-spin shadow-[0_0_20px_rgba(0,134,211,0.25)]"></div>
                                <div className="absolute inset-0 w-[48px] h-[48px] border-[3px] border-transparent border-b-[#4DBDFF]/30 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                            </div>
                            <div className="text-center">
                                <p className="text-white text-[18px] font-semibold mb-[4px]">{gameState.game?.name}</p>
                                <p className="text-white/30 text-[13px] font-medium animate-pulse">{t('game.loadingGame')}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Game
